/** @format */

import React from "react";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  QuestionCircleOutlined,
  SendOutlined,
  StopOutlined,
  SyncOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  TIntermittentAction,
  TIntermittentCommunication,
  TIntermittentStatus,
} from "../../../redux-modules/declarations/maps/intermittentCommunications";

interface StatusMeta {
  label: string;
  /** Colore preset dei Tag antd: segue il tema dell'applicazione. */
  color: string;
  icon: JSX.Element;
  needsAction: boolean;
}

/** Etichette e colori degli stati (codici identici al backend). */
export const STATUS_META: Record<TIntermittentStatus, StatusMeta> = {
  scheduled: { label: "In programma", color: "blue", icon: <ClockCircleOutlined />, needsAction: false },
  blocked: { label: "Bloccata", color: "red", icon: <StopOutlined />, needsAction: true },
  needs_confirmation: { label: "Da confermare", color: "orange", icon: <ExclamationCircleOutlined />, needsAction: true },
  excluded: { label: "Esclusa", color: "default", icon: <MinusCircleOutlined />, needsAction: false },
  sending: { label: "Invio in corso", color: "processing", icon: <SyncOutlined spin />, needsAction: false },
  sent: { label: "Inviata", color: "cyan", icon: <SendOutlined />, needsAction: false },
  worked: { label: "Presenza verificata", color: "green", icon: <CheckCircleOutlined />, needsAction: false },
  cancellation_pending: { label: "In attesa di annullamento", color: "gold", icon: <WarningOutlined />, needsAction: true },
  cancelling: { label: "Annullamento in corso", color: "processing", icon: <SyncOutlined spin />, needsAction: false },
  cancelled: { label: "Annullata", color: "default", icon: <CloseCircleOutlined />, needsAction: false },
  void: { label: "Non necessaria", color: "default", icon: <MinusCircleOutlined />, needsAction: false },
  expired: { label: "Scaduta", color: "red", icon: <ExclamationCircleOutlined />, needsAction: true },
  failed: { label: "Errore di invio", color: "red", icon: <CloseCircleOutlined />, needsAction: true },
  uncertain: { label: "Esito incerto", color: "volcano", icon: <QuestionCircleOutlined />, needsAction: true },
  deadline_missed: { label: "Termine superato", color: "magenta", icon: <ExclamationCircleOutlined />, needsAction: true },
};

/** Ordine degli stati nei filtri: prima quelli da gestire. */
export const STATUS_ORDER: TIntermittentStatus[] = [
  "blocked",
  "needs_confirmation",
  "cancellation_pending",
  "failed",
  "uncertain",
  "deadline_missed",
  "expired",
  "scheduled",
  "sent",
  "worked",
  "cancelled",
  "excluded",
  "void",
  "sending",
  "cancelling",
];

interface ActionMeta {
  label: string;
  /** Titolo della finestra di conferma */
  title: string;
  /** Cosa succede dopo la conferma, in parole semplici */
  description: string;
  okText: string;
  danger?: boolean;
  primary?: boolean;
  noteRequired?: boolean;
  notePlaceholder?: string;
}

export const ACTION_META: Record<TIntermittentAction, ActionMeta> = {
  confirm: {
    label: "Conferma e invia",
    title: "Confermare l'invio?",
    description:
      "Il turno è stato inserito a meno di 24 ore dall'inizio. Confermando, la comunicazione parte subito.",
    okText: "Conferma e invia",
    primary: true,
  },
  send_now: {
    label: "Invia ora",
    title: "Inviare subito la comunicazione?",
    description: "La comunicazione parte adesso, senza aspettare l'invio automatico.",
    okText: "Invia ora",
    primary: true,
  },
  exclude: {
    label: "Escludi",
    title: "Escludere la comunicazione?",
    description:
      "La comunicazione non verrà inviata. Se il dipendente lavora comunque, il turno resterà senza comunicazione.",
    okText: "Escludi",
    danger: true,
    noteRequired: true,
    notePlaceholder: "Motivo dell'esclusione",
  },
  restore: {
    label: "Ripristina",
    title: "Ripristinare la comunicazione?",
    description: "La comunicazione torna in programma e partirà in automatico.",
    okText: "Ripristina",
    primary: true,
  },
  cancel_now: {
    label: "Annulla ora",
    title: "Inviare subito l'annullamento?",
    description:
      "Parte la comunicazione di annullamento per questo giorno. L'operazione non si può annullare.",
    okText: "Invia annullamento",
    danger: true,
    notePlaceholder: "Motivo (facoltativo)",
  },
  mark_worked: {
    label: "Era presente",
    title: "Segnare il lavoratore come presente?",
    description:
      "L'annullamento non verrà inviato e il caso viene chiuso. Ricordati di inserire anche la presenza, se manca.",
    okText: "Era presente",
    primary: true,
    noteRequired: true,
    notePlaceholder: "Es. presente, timbratura mancata per problema dell'app",
  },
  retry: {
    label: "Riprova",
    title: "Riprovare l'invio?",
    description:
      "La mail viene inviata di nuovo. Se l'esito era incerto, verifica prima nella posta inviata per evitare un doppio invio.",
    okText: "Riprova",
    primary: true,
  },
  mark_sent: {
    label: "Segna come inviata",
    title: "Segnare come inviata?",
    description:
      "Usalo se la comunicazione è partita in altro modo (portale, app) o se hai verificato che la mail è stata inviata.",
    okText: "Segna come inviata",
    noteRequired: true,
    notePlaceholder: "Es. inviata dal portale del Ministero alle 9:30",
  },
};

/** Ordine dei pulsanti nel dettaglio. */
export const ACTION_ORDER: TIntermittentAction[] = [
  "confirm",
  "send_now",
  "retry",
  "mark_worked",
  "cancel_now",
  "mark_sent",
  "restore",
  "exclude",
];

export const EVENT_LABELS: Record<string, string> = {
  created: "Comunicazione creata",
  blocked: "Bloccata per dati mancanti",
  unblocked: "Dati completati",
  shift_attached: "Turno collegato",
  shift_detached: "Turno scollegato",
  voided: "Non più necessaria",
  send_started: "Invio avviato",
  sent: "Comunicazione inviata",
  send_failed: "Invio non riuscito",
  cancel_started: "Annullamento avviato",
  cancelled: "Annullamento inviato",
  cancel_failed: "Annullamento non riuscito",
  presence_verified: "Presenza verificata",
  cancellation_pending: "In attesa di annullamento",
  cancellation_revoked: "Annullamento evitato",
  expired: "Turno iniziato senza invio",
  deadline_missed: "Termine di annullamento superato",
  uncertain: "Esito dell'invio incerto",
  reminder: "Sollecito inviato",
  manual_action: "Azione manuale",
  inps_code_updated: "Codice INPS aggiornato",
};

export const DETACH_REASONS: Record<string, string> = {
  shift_deleted: "turno eliminato",
  not_working_type: "non più un turno lavorativo",
  moved: "spostato su altra data o dipendente",
};

// ── Formattazione date ───────────────────────────────────────────────

export const fmtDay = (d?: string | null): string =>
  d ? moment(d).format("ddd DD/MM/YYYY") : "-";

export const fmtDateTime = (d?: string | null): string =>
  d ? moment(d).format("ddd DD/MM HH:mm") : "-";

export const fmtShift = (c: Pick<TIntermittentCommunication, "shift_start_at" | "shift_end_at">): string => {
  const start = moment(c.shift_start_at);
  const end = moment(c.shift_end_at);
  return `${start.format("HH:mm")} – ${end.format("HH:mm")}`;
};

export const isOvernight = (c: Pick<TIntermittentCommunication, "shift_start_at" | "shift_end_at">): boolean =>
  !moment(c.shift_end_at).isSame(moment(c.shift_start_at), "day");

export const fullName = (c: TIntermittentCommunication): string =>
  `${c.user_surname ?? c.user?.surname ?? ""} ${c.user_name ?? c.user?.name ?? ""}`.trim();

/** Ore (per difetto) tra adesso e la data indicata. */
export const hoursFromNow = (d?: string | null): number =>
  d ? Math.max(0, Math.floor(moment(d).diff(moment(), "minutes") / 60)) : 0;

/** "tra 5 ore", "tra 1 ora", "tra meno di un'ora". */
export const inHoursLabel = (d?: string | null): string => {
  const h = hoursFromNow(d);
  if (h < 1) return "tra meno di un'ora";
  return `tra ${h} ${h === 1 ? "ora" : "ore"}`;
};

export type TNextStepTone = "danger" | "warning" | "default" | "secondary";

/** Il prossimo passo della comunicazione, in una riga. */
export const nextStep = (c: TIntermittentCommunication): { text: string; tone: TNextStepTone } => {
  switch (c.status) {
    case "scheduled":
      return moment(c.send_due_at).isAfter(moment())
        ? { text: `Invio automatico ${inHoursLabel(c.send_due_at)} (${fmtDateTime(c.send_due_at)})`, tone: "default" }
        : { text: "Invio automatico a breve", tone: "default" };
    case "blocked":
      return { text: c.block_reason || "Dati obbligatori mancanti", tone: "danger" };
    case "needs_confirmation":
      return { text: `Da confermare: il turno inizia tra ${hoursFromNow(c.shift_start_at)} ore`, tone: "warning" };
    case "sent":
      return { text: `Controllo presenze ${fmtDateTime(c.cancel_alert_at)}`, tone: "secondary" };
    case "worked":
      return { text: "Presenza verificata", tone: "secondary" };
    case "cancellation_pending": {
      const h = c.hours_to_auto_cancel ?? hoursFromNow(c.cancel_due_at);
      return { text: `Annullamento automatico tra ${h} ${h === 1 ? "ora" : "ore"} (${fmtDateTime(c.cancel_due_at)})`, tone: "warning" };
    }
    case "cancelled":
      return { text: `Annullata ${fmtDateTime(c.cancelled_at)}`, tone: "secondary" };
    case "excluded":
      return { text: c.note ? `Esclusa: ${c.note}` : "Esclusa manualmente", tone: "secondary" };
    case "void":
      return { text: "Turno rimosso prima dell'invio", tone: "secondary" };
    case "expired":
      return { text: "Turno iniziato senza comunicazione", tone: "danger" };
    case "failed":
      return { text: "Invio non riuscito dopo 3 tentativi", tone: "danger" };
    case "uncertain":
      return { text: "Verificare nella posta inviata se la mail è partita", tone: "danger" };
    case "deadline_missed":
      return { text: "Termine di legge superato: decidere manualmente", tone: "danger" };
    default:
      return { text: c.status_label, tone: "secondary" };
  }
};
